const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_SECONDS = DAY_MS / 1000;

export const UPTIME_HISTORY_DAYS = 90;

const toUtcString = (date) => date.toISOString().replace(".000Z", "Z");

const roundToOneDecimal = (value) => Math.round(value * 10) / 10;

// Billing works on calendar months in UTC, so the uptime figures do too.
export const currentUtcMonth = (now = new Date()) => {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { start: toUtcString(start), end: toUtcString(end) };
};

export const utcMonthName = (locale, now = new Date()) =>
  new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(new Date(currentUtcMonth(now).start));

export const formatUptimeNumber = (value, locale) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);

// From UTC midnight (days - 1) days ago up to now: one full bar per day, today's bar so far.
export const uptimeHistoryWindow = (now = new Date(), days = UPTIME_HISTORY_DAYS) => {
  const todayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return { start: toUtcString(new Date(todayStart - (days - 1) * DAY_MS)), end: toUtcString(now) };
};

export const uptimeSummary = (uptime) => {
  if (!uptime || !uptime.periodSeconds) {
    return null;
  }
  return {
    hours: roundToOneDecimal(uptime.connectedSeconds / 3600),
    percent: roundToOneDecimal((100 * uptime.connectedSeconds) / uptime.periodSeconds),
    thresholdHours: roundToOneDecimal(uptime.billingThresholdSeconds / 3600),
    // The billing rule as a share of the month (15 %), the same in every month.
    thresholdShare: roundToOneDecimal((100 * uptime.billingThresholdSeconds) / uptime.periodSeconds),
    // Rounded up, so a device just below the threshold never shows "0 h to go".
    remainingHours: Math.ceil((Math.max(0, uptime.billingThresholdSeconds - uptime.connectedSeconds) / 3600) * 10) / 10,
    // How far the device is on its way to the billing threshold, capped at 100.
    thresholdProgress: Math.min(
      100,
      roundToOneDecimal((100 * uptime.connectedSeconds) / uptime.billingThresholdSeconds)
    ),
    reached: uptime.billingThresholdReached,
  };
};

// One bar per day: the share of the day the device was connected. Today's bucket ends
// at "now", so its share is measured against the part of the day that has passed.
export const dailyUptimeBars = (buckets) =>
  (buckets || [])
    .filter((bucket) => bucket.periodSeconds > 0)
    .map((bucket) => ({
      start: new Date(bucket.start),
      hours: roundToOneDecimal(bucket.connectedSeconds / 3600),
      percent: roundToOneDecimal((100 * bucket.connectedSeconds) / bucket.periodSeconds),
      partial: bucket.periodSeconds < DAY_SECONDS,
    }));

export const averageUptimePercent = (buckets) => {
  const period = (buckets || []).reduce((sum, bucket) => sum + bucket.periodSeconds, 0);
  if (!period) {
    return null;
  }
  const connected = buckets.reduce((sum, bucket) => sum + bucket.connectedSeconds, 0);
  return roundToOneDecimal((100 * connected) / period);
};
